import {useLiveQuery} from 'dexie-react-hooks';
import {Button} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {deleteRequest, retryItem} from 'offline/processQueue';
import {getDisplayStringFromKey, OfflineSyncStatus} from 'offline/offlineQueueUtils';
import {getAllOfflineItems} from 'offline/offlineQueueFns';
import {
	DeleteOutline,
	EqualizerOutlined
} from '@material-ui/icons';
import {
	AccessTime,
	ChecklistOutlined,
	DescriptionOutlined,
	ErrorOutlineOutlined,
	FileUploadOutlined
} from '@mui/icons-material';


export default function DataSyncDialog() {
	// 🔥 Reactively listen to IndexedDB
	const uploads = useLiveQuery(async () => await getAllOfflineItems(), []);
	
	if (!uploads) return <div className="p-10 text-gray-400">Loading...</div>;

	const getEntityIcon = (entityType) => {
		const iconMap = {
			3: <EqualizerOutlined className="text-black" />,
			4: <ChecklistOutlined className="text-black" />,
			5: <ChecklistOutlined className="text-black" />
		};
		return iconMap[entityType] || <DescriptionOutlined className="text-black" />;
	};

	return (
		<>
			<div className="flex flex-col h-full">
				<div className=" shrink-0">
					<div className="text-white text-sm font-medium">Requests</div>
					<div className="text-xs text-gray-400 mt-3">
						{uploads.length} total • {uploads.filter(u => u.status === OfflineSyncStatus.PENDING).length}{' '}
						pending
					</div>
				</div>

				<div className="mt-10 flex-1 overflow-y-auto space-y-6 pl-3 pb-14">
					{uploads.map((file, idx) => (
						<div key={idx} className={`rounded-xl shadow-sm border border-solid border-l-4 p-10 bg-white `}>
							<div className="flex items-start gap-7 w-full">
								<div className="w-24 h-24 rounded-full bg-grey-200 p-5 flex items-center justify-center  text-black">
									{getEntityIcon(file.entityType)}
								</div>

								<div className="flex flex-col gap-10 ">
									<span className="font-medium text-black capitalize">
										{getDisplayStringFromKey(file.key)}
										{/* {file.key} */}
									</span>
									{file.status === OfflineSyncStatus.PENDING && (
										<div className="flex gap-8 items-center">
											<AccessTime className="text-black" sx={{fontSize: 17}} />
											<span class="rounded-full bg-purple-50 px-7 py-2 text-xs font-medium text-purple-500 border border-solid border-purple-100">
												Pending
											</span>
										</div>
									)}

									{file.status === OfflineSyncStatus.IN_PROGRESS && (
										<div className="flex gap-8 items-center">
											<FileUploadOutlined className="text-black" sx={{fontSize: 17}} />
											<span class="rounded-full bg-orange-50 px-7 py-2 text-xs font-medium text-orange-500 border border-solid border-orange-100">
												Uploading<span className="animate-dots">.</span>
											</span>
										</div>
									)}

									{file.status === OfflineSyncStatus.SUCCESS && (
										<div className="flex gap-8 items-center">
											<CheckCircleIcon className="text-green-400" sx={{fontSize: 17}} />
											<span class="rounded-full bg-green-50 px-7 py-2 text-xs font-medium text-green-500 border border-solid border-green-100">
												Successfully updated
											</span>
										</div>
									)}

									{file.status === OfflineSyncStatus.FAILED && (
										<div className="flex gap-8 items-center">
											<ErrorOutlineOutlined className="text-black" sx={{fontSize: 17}} />
											<span class="rounded-full bg-red-50 px-7 py-2 text-xs font-medium text-red-500 border border-solid border-red-100">
												{file.error}
											</span>
										</div>
									)}

									<div className="flex flex-col gap-4">
										<div className="grid grid-cols-2">
											<div className="flex col-span-1 gap-4 items-center">
												<span className="text-gray-500 text-xs font-medium">Retries:</span>
												<span className="text-gray-500 text-xs font-medium">
													{file.retryCount ? file.retryCount : 0}
												</span>
											</div>

											<div className="flex gap-4 items-center col-span-1">
												<span className="text-gray-500 text-xs font-medium">Created At:</span>
												<span className="text-gray-500 text-xs font-medium">
													{new Date(file.createdAt).toLocaleTimeString([], {
														hour: '2-digit',
														minute: '2-digit'
													})}
												</span>
											</div>
										</div>


											<div className="flex gap-4 items-center">
												<span className="text-gray-500 text-xs font-medium">Last Request:</span>
												<span className="text-gray-500 text-xs font-medium">
													{file.lastReqTime ? new Date(file.lastReqTime).toLocaleTimeString([], {
														hour: '2-digit',
														minute: '2-digit'
													}) : '__:__'}
												</span>
											</div>
									</div>

									{file.status === OfflineSyncStatus.FAILED && (
										<div className="flex items-center justify-center gap-6">
											<Button
												variant="outlined"
												size="small"
												startIcon={<ReplayIcon fontSize="small" />}
												className=""
												style={{
													borderColor: 'black', // Tailwind gray-200
													color: 'black',
													textTransform: 'none',
													backgroundColor: 'white',
													borderRadius: '5px',
													fontSize: '10px', // smaller text
													padding: '2px 6px', // tighter padding
													minWidth: 'auto', // remove default MUI min-width
													lineHeight: '1.2'
												}}
												onClick={() => retryItem(file.id)}
											>
												Retry
											</Button>
											<Button
												variant="outlined"
												size="small"
												startIcon={<DeleteOutline fontSize="small" />}
												className=""
												style={{
													borderColor: 'black', // Tailwind gray-200
													color: 'black',
													textTransform: 'none',
													backgroundColor: 'white',
													borderRadius: '5px',
													fontSize: '10px', // smaller text
													padding: '2px 6px', // tighter padding
													minWidth: 'auto', // remove default MUI min-width
													lineHeight: '1.2'
												}}
												onClick={() => deleteRequest(file.id)}
											>
												Delete
											</Button>
										</div>
									)}
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</>
	);
}

{
	/* Retry count and Last Request at the bottom */
}
{
	/* <div className="grid gap-2 ">
							{file.status !== OfflineSyncStatus.SUCCESS && (
								<span className="text-sm text-black ">Retry count: {file.retryCount}</span>
							)}
							{file.lastReqTime && (	
							<span  className="text-xs text-gray-500 ">Last Request: {new Date(file.lastReqTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
							)}
							<span className="text-xs text-gray-500">
								Created At: {new Date(file.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
							</span>
						</div> */
}

// old version of request card
// 				<div
// 					key={idx}
// 					className={`border border-solid rounded-lg p-10 flex flex-col gap-4 border-indigo-200 bg-white ${file.status === OfflineSyncStatus.FAILED ? '' : ''} `}
// 				>
// 					{/* Top row */}
// 					<div className="flex justify-between items-center ">
// 						<div className="flex items-center gap-7">
// 							{file.status === OfflineSyncStatus.SUCCESS && (
// 								<CheckCircleIcon className="text-green-500" fontSize="medium" />
// 							)}
// 							{file.status === OfflineSyncStatus.FAILED && (
// 								<ErrorIcon className="text-red-500" fontSize="medium" />
// 							)}
// 							{(file.status === OfflineSyncStatus.IN_PROGRESS ||
// 								file.status === OfflineSyncStatus.PENDING) && (
// 								<CloudUploadIcon className="ml-2 text-indigo-500" fontSize="medium" />
// 								)}

// 							<span className="font-medium text-black capitalize">{getDisplayStringFromKey(file.key)}</span>
// 						</div>
// 					</div>

// 					{/* Status & Progress */}
// 					{file.status === OfflineSyncStatus.PENDING && (
// 						<div className="w-full bg-indigo-100 px-5 py-3 rounded-6 my-3">
// 							<span className="text-xs font-medium text-indigo-600 mt-1">Pending</span>
// 							</div>
// 					)}

// 					{file.status === OfflineSyncStatus.IN_PROGRESS && (
// 						<div className="w-full bg-indigo-100 px-5 py-3 rounded-6 my-3">
// 							<span className="text-xs font-medium text-indigo-600 mt-1">
// 								Uploading<span className="animate-dots">.</span>
// 							</span></div>
// 					)}

// 					{file.status === OfflineSyncStatus.SUCCESS && (
// 						<div className="w-full bg-green-100 px-5 py-3 rounded-6 my-3">
// 							<span className="text-xs font-medium text-green-600 mt-1">Successfully updated </span>
// 							</div>
// 					)}

// 					{file.status === OfflineSyncStatus.FAILED && (
// 						<div className="w-full bg-red-100 px-5 py-3 rounded-6 my-3">
// 							<span className="text-xs font-medium text-red-600  -mt-1 capitalize">{file.error}</span>

// 						</div>
// 					)}

// 					<div className="grid gap-3 text-sm">
// 						{file.status !== OfflineSyncStatus.SUCCESS && (
// 							<div className="flex gap-4">
// 								<span className="text-gray-500 text-sm">Retries:</span>
// 								<span className="text-black text-sm font-medium">{file.retryCount}</span>
// 							</div>
// 						)}

// 						<div className="flex justify-between items-center">
// 						{file.lastReqTime && (
// 							<div className="flex flex-col">
// 									<span className="text-gray-500 text-sm">Last Request:</span>
// 								<div className="text-black text-sm font-medium">
// 									{new Date(file.lastReqTime).toLocaleTimeString([], {
// 										hour: "2-digit", minute: "2-digit" })}
// 								</div>
// 							</div>
// 						)}

// 							<div className={`flex ${file.lastReqTime ? 'flex-col' : 'gap-4'}`}>
// 								<span className="text-gray-500 text-sm">Created At:</span>
// 								<span className="text-black text-sm font-medium">
// 								{new Date(file.createdAt).toLocaleTimeString([], {
// 									hour: "2-digit",
// 									minute: "2-digit",
// 								})}
// 							</span>
// 						</div>
// 						</div>

// 					</div>

// 					{file.status === OfflineSyncStatus.FAILED && (
// 						<div className="flex items-center justify-center gap-6">
// 							<Button
// 								variant="outlined"
// 								size="small"
// 								startIcon={<ReplayIcon fontSize="small" />}
// 								className=""
// 								style={{
// 									borderColor: 'black', // Tailwind gray-200
// 									color: 'black',
// 									textTransform: 'none',
// 									backgroundColor: 'white',
// 									borderRadius: '5px',
// 									fontSize: '10px', // smaller text
// 									padding: '2px 6px', // tighter padding
// 									minWidth: 'auto', // remove default MUI min-width
// 									lineHeight: '1.2'
// 								}}
// 								onClick={() => retryItem(file.id)}
// 							>
// 								Retry
// 							</Button>
// 							<Button
// 								variant="outlined"
// 								size="small"
// 								startIcon={<DeleteOutline fontSize="small" />}
// 								className=""
// 								style={{
// 									borderColor: 'black', // Tailwind gray-200
// 									color: 'black',
// 									textTransform: 'none',
// 									backgroundColor: 'white',
// 									borderRadius: '5px',
// 									fontSize: '10px', // smaller text
// 									padding: '2px 6px', // tighter padding
// 									minWidth: 'auto', // remove default MUI min-width
// 									lineHeight: '1.2'
// 								}}
// 								onClick={() => deleteRequest(file.id)}
// 							>
// 								Delete
// 							</Button>
// 						</div>
// )}

// 				</div>
